import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { money, pct, dateShort, marginColor } from "@/lib/format";
import { STATUS_COLORS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import CustomerMap from "@/components/CustomerMap";
import BolViewerDialog from "@/components/workspace/BolViewerDialog";
import ContextDiscussion from "@/components/chat/ContextDiscussion";
import { ArrowLeft, Building2, Mail, Phone, MapPin, Plus, User, ClipboardList } from "lucide-react";

const WON = ["won", "booked"];

export default function CustomerDetail() {
  const { company } = useParams();
  const decoded = decodeURIComponent(company || "");
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bolQuote, setBolQuote] = useState(null);

  useEffect(() => {
    const key = decoded.trim().toLowerCase();
    Promise.all([
      api.get("/addresses", { params: { search: decoded } }),
      api.get("/quotes", { params: { search: decoded } }),
    ])
      .then(([a, q]) => {
        setAddresses((a.data || []).filter((x) => (x.company || "").trim().toLowerCase() === key));
        setQuotes((q.data || []).filter((x) => (x.customer_name || "").trim().toLowerCase() === key));
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [decoded]);

  const contact = useMemo(() => {
    const a = addresses.find((x) => x.contact_name || x.contact_email || x.contact_phone) || {};
    return { name: a.contact_name, email: a.contact_email, phone: a.contact_phone };
  }, [addresses]);

  const stats = useMemo(() => {
    const totalQuoted = quotes.reduce((s, q) => s + (q.sell_price || 0), 0);
    const won = quotes.filter((q) => WON.includes(q.status));
    const totalWon = won.reduce((s, q) => s + (q.sell_price || 0), 0);
    return { locations: addresses.length, quoteCount: quotes.length, totalQuoted, wonCount: won.length, totalWon };
  }, [addresses, quotes]);

  const startQuote = () => {
    const p = addresses[0] || {};
    navigate("/workspace", {
      state: {
        prefill: {
          customer_name: decoded,
          destination_zip: p.zip || "",
          dest_city: p.city || "",
          dest_state: p.state || "",
          delivery_location_type: p.location_type || "commercial",
        },
      },
    });
  };

  const sortedQuotes = [...quotes].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));

  return (
    <div className="space-y-5 animate-fade-up" data-testid="customer-detail-page">
      <button onClick={() => navigate("/customers")} className="flex items-center gap-1 text-sm text-muted-foreground transition hover:text-foreground" data-testid="customer-back">
        <ArrowLeft className="h-4 w-4" /> Customers
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-border bg-card p-5">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
            <Building2 className="h-7 w-7" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-black tracking-tighter" data-testid="customer-name">{decoded}</h1>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {contact.name && <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{contact.name}</span>}
              {contact.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{contact.email}</span>}
              {contact.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{contact.phone}</span>}
            </div>
          </div>
        </div>
        <Button onClick={startQuote} data-testid="customer-new-quote"><Plus className="mr-1.5 h-4 w-4" />New Quote</Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ["Locations", stats.locations],
          ["Quotes", stats.quoteCount],
          ["Total Quoted", money(stats.totalQuoted)],
          ["Won", `${stats.wonCount} · ${money(stats.totalWon)}`],
        ].map(([l, v]) => (
          <div key={l} className="rounded-lg border border-border bg-card p-4">
            <div className="label-caps">{l}</div>
            <div className="metric-num mt-1 text-xl font-bold">{v}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-1">
          <CustomerMap locations={addresses} />
          <div className="rounded-lg border border-border bg-card">
            <div className="border-b border-border px-4 py-3 font-heading text-base font-bold">Locations ({addresses.length})</div>
            <div className="max-h-[360px] space-y-2 overflow-y-auto p-3">
              {addresses.length === 0 && <div className="px-1 py-4 text-sm text-muted-foreground">No saved addresses.</div>}
              {addresses.map((l) => (
                <div key={l.id} className="rounded-md border border-border p-3 text-sm" data-testid={`customer-loc-${l.id}`}>
                  <div className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--primary))]" />
                    <div>
                      <div className="font-medium">{l.address1 || "—"}</div>
                      <div className="text-muted-foreground">{[l.city, l.state].filter(Boolean).join(", ")} {l.zip}</div>
                      {(l.contact_name || l.contact_phone) && (
                        <div className="mt-1 text-xs text-muted-foreground">{[l.contact_name, l.contact_phone].filter(Boolean).join(" · ")}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="rounded-lg border border-border bg-card">
            <div className="border-b border-border px-4 py-3 font-heading text-base font-bold">Quotes ({quotes.length})</div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>Date</TableHead>
                    <TableHead>Quote #</TableHead>
                    <TableHead>Lane</TableHead>
                    <TableHead className="text-right">Sell</TableHead>
                    <TableHead className="text-right">Margin</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Rep</TableHead>
                    <TableHead className="text-center">BOL</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
                  )}
                  {!loading && sortedQuotes.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">No quotes yet for this customer.</TableCell></TableRow>
                  )}
                  {sortedQuotes.map((q) => (
                    <TableRow key={q.id} className="cursor-pointer border-border hover:bg-secondary/40" onClick={() => navigate(`/quotes/${q.id}`)} data-testid={`customer-quote-${q.quote_number}`}>
                      <TableCell className="text-xs text-muted-foreground">{dateShort(q.created_at)}</TableCell>
                      <TableCell className="metric-num text-sm font-semibold text-[hsl(var(--primary))]">{q.quote_number}</TableCell>
                      <TableCell className="metric-num text-xs text-muted-foreground">{q.origin_zip || "—"}→{q.destination_zip || "—"}</TableCell>
                      <TableCell className="metric-num text-right text-sm font-semibold">{money(q.sell_price)}</TableCell>
                      <TableCell className={`metric-num text-right text-sm ${marginColor((q.financial || {}).margin_health)}`}>{pct(q.margin_pct)}</TableCell>
                      <TableCell><span className={`inline-block rounded border px-2 py-0.5 text-[11px] font-bold uppercase ${STATUS_COLORS[q.status] || ""}`}>{q.status}</span></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{q.created_by}</TableCell>
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        {q.bol_pro_number ? (
                          <button
                            onClick={() => setBolQuote(q)}
                            data-testid={`customer-bol-${q.quote_number}`}
                            title={`View R+L BOL ${q.bol_pro_number}`}
                            className="inline-flex items-center rounded bg-[hsl(var(--success))]/15 px-1.5 py-1 text-[hsl(var(--success))] transition-colors hover:bg-[hsl(var(--success))]/25"
                          >
                            <ClipboardList className="h-3.5 w-3.5" />
                          </button>
                        ) : (
                          <span className="text-xs text-muted-foreground/40">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      <ContextDiscussion
        refType="customer"
        refId={decoded}
        attach={{ type: "customer", id: decoded, label: decoded, sub: contact.name || "" }}
        title="Customer Discussion"
      />

      <BolViewerDialog open={!!bolQuote} onOpenChange={(v) => { if (!v) setBolQuote(null); }} quote={bolQuote} />
    </div>
  );
}
